import { useEffect, useMemo } from 'react';
import DocumentTitle from 'react-document-title';
import { useNavigate } from 'react-router-dom';

import { Loader } from '@hivebrite/design-system/Loader';

import MentorsRenderer from 'hb-react/frontoffice/apps/Mentoring/container/MentorsRenderer';
import { useMentoringOutletContext } from 'hb-react/frontoffice/apps/Mentoring/container/ProgramControlledAccess';
import { useGetMentorsCustomAttributes } from 'hb-react/frontoffice/apps/Mentoring/hooks/useGetMentorsCustomAttributes';
import PageNotFound from 'hb-react/frontoffice/apps/Mentoring/pages/PageNotFound';
import type { Program } from 'hb-react/frontoffice/apps/Mentoring/types';
import { flattenAttributes } from 'hb-react/frontoffice/apps/MyMentoring/utils/flattenAttributesUtils';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';
import { getPageTitleFromNetwork } from 'hb-react/shared/utils/getPageTitleFromNetwork';
import type { User } from 'hb-react/types';

interface MentorsIndexProps {
  program: Program;
  currentUser: User;
  disabledAccess?: boolean;
}

const MentorsIndex = ({
  program,
  currentUser,
  disabledAccess,
}: MentorsIndexProps) => {
  const { data: customAttributes, isLoading: isLoadingAttributes } =
    useGetMentorsCustomAttributes({
      program,
      queryConfig: {
        is_displayed_in_search_filters: true,
      },
    });

  const attributes = useMemo(
    () => flattenAttributes(customAttributes),
    [customAttributes],
  );

  if (isLoadingAttributes) {
    return <Loader size={40} />;
  }

  return (
    <MentorsRenderer
      program={program}
      attributes={attributes}
      currentUser={currentUser}
      disabledAccess={disabledAccess}
    />
  );
};

interface MentorsIndexControlledAccessProps {
  disabledAccess?: boolean;
}

const MentorsIndexControlledAccess = ({
  disabledAccess,
}: MentorsIndexControlledAccessProps) => {
  const { currentNetwork, currentUser } = useDefaultAppContexts();
  const { program } = useMentoringOutletContext();
  const navigate = useNavigate();
  const { topic } = useTopicContext();

  useEffect(() => {
    if (program && program.mode === 'admin_match') {
      navigate('/');
    }
  }, [program, navigate]);

  if (!program || !currentUser) {
    return <PageNotFound />;
  }

  return (
    <>
      <DocumentTitle
        title={generatePageTitle({
          title: program.name,
          networkName: getPageTitleFromNetwork(currentNetwork),
          // @ts-expect-error topic type should we reworked
          topicName: topic?.name,
        })}
      />
      <MentorsIndex
        program={program}
        currentUser={currentUser}
        disabledAccess={disabledAccess}
      />
    </>
  );
};

export default MentorsIndexControlledAccess;
